import React from "react";
import { Box, Typography, useMediaQuery, useTheme, Stack } from "@mui/material";
import { motion } from "framer-motion";
import missionImg from "./mission1.jpg";
import visionImg from "./vision1.png";
import missionIcon from "./missionIcon.png";
import visionIcon from "./visionIcon.png";

const fadeInVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const VisionMissionValues = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const data = [
    {
      title: "Our Mission",
      image: missionImg,
      icon: missionIcon,
      reverse: true,
      id: "our_mission",
      content:
        "We aim to simplify how businesses and governments work-by offering a unified WhatsApp-first platform powered by AI, automation and seamless integrations.",
      points: [
        {
          icon: "✅",
          text: "WhatsApp-first platform for unified workflows.",
        },
        {
          icon: "🤖",
          text: "AI tools for smart communication and insights.",
        },
        {
          icon: "🔁",
          text: "Streamlines complex processes effortlessly.",
        },
        {
          icon: "🚀",
          text: "Enables fast, scalable digital growth.",
        },
        {
          icon: "🌐",
          text: "Inclusive and mobile-first for all users.",
        },
        {
          icon: "🔗",
          text: "Integrates easily with CRMs and payments.",
        },
      ],
    },
    {
      title: "Our Vision",
      image: visionImg,
      icon: visionIcon,
      reverse: false,
      id: "our_vision",
      content:
        "To become the go-to platform where all workflows-business or government-run through a single smart WhatsApp-first system.",
      points: [
        {
          icon: "📦",
          text: "10 built-in suites for logistics, billing, commerce, and more",
        },
        {
          icon: "💬",
          text: "Conversations power all business and government workflows",
        },
        {
          icon: "⚙️",
          text: "A unified platform replacing disconnected tools",
        },
        {
          icon: "✨",
          text: "Automation that feels seamless and intuitive",
        },
        {
          icon: "🌍",
          text: "Optimized systems for every sector, everywhere",
        },
      ],
    },
  ];

  return (
    <Box
      component="section"
      aria-label="Vision and Mission - Vertex Suite"
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "100%", sm: "90%", md: "1280px" },
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          py: { xs: 2, sm: 3, md: 3, lg: 4 },
          mx: "auto",
        }}
      >
        {data.map((item, index) => (
          <motion.div
            key={index}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInVariant}
          >
            <Box
              id={item.id}
              component="article"
              sx={{
                display: "flex",
                flexDirection: item.reverse
                  ? { xs: "column-reverse", md: "row" }
                  : { xs: "column-reverse", md: "row-reverse" },
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 2, sm: 4, md: 6 },
                mb: { xs: 4, sm: 6, md: 4 },
              }}
            >
              {/* Image Section */}
              <Box
                sx={{
                  width: { xs: "100%", md: "50%" },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={item.image}
                  alt={item.title}
                  sx={{
                    width: { xs: "100%", sm: "90%", md: "85%" },
                    height: "auto",
                    transform: {
                      xs: "none",
                      md: item.reverse
                        ? "translateX(-20px)"
                        : "translateX(12px)",
                    },
                    transition: "transform 0.3s ease",
                  }}
                />
              </Box>

              {/* Text Section */}
              <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                {/* Title with Vertical Line */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  mb={{ xs: 1.5, sm: 2, md: 1.5, lg: 2 }}
                  mt={{ xs: 0, sm: 0, md: 0, lg: 0 }}
                >
                  <Box
                    sx={{
                      width: "6px",
                      height: { xs: "22px", sm: "36px", md: "30px" },
                      backgroundColor: "#054E91",
                      borderTopRightRadius: "6px",
                      borderBottomRightRadius: "6px",
                      mr: 2,
                    }}
                  />
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontSize: { xs: "22px", sm: "28px", md: "34px" },
                      color: "#054E91",
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {item.title}
                    <Box
                      component="img"
                      src={item.icon}
                      alt={`${item.title} Icon`}
                      sx={{
                        width: { xs: "22px", sm: "26px", md: "28px" },
                        height: { xs: "22px", sm: "26px", md: "28px" },
                      }}
                    />
                  </Typography>
                </Stack>

                {/* Paragraph */}
                <Typography
                  sx={{
                    fontSize: { xs: "15px", sm: "17px", md: "20px" },
                    fontWeight: "400",
                    lineHeight: { xs: 1.5, sm: 1.8, md: 1.3 },
                    fontFamily: "'Roboto', sans-serif",
                    color: "text.secondary",
                    letterSpacing: "0.3px",
                    mb: { xs: 2, sm: 2 },
                  }}
                >
                  {item.content}
                </Typography>

                {/* Icon-Based Bullet Points */}
                <Stack spacing={1.2} sx={{ mt: 1 }}>
                  {item.points?.map((point, idx) => (
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={1.2}
                      key={idx}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "18px",
                          mt: "2px",
                          minWidth: "24px",
                        }}
                      >
                        {point.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "15px", sm: "17px" },
                          fontWeight: "400",
                          fontFamily: "'Roboto', sans-serif",
                          color: "text.secondary",
                          lineHeight: { xs: 1.5, sm: 1.8 },
                          letterSpacing: "0.3px",
                        }}
                      >
                        {point.text}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Box>
  );
};

export default VisionMissionValues;